import React from 'react';
import Card from '../Card/Card';
import { useCurrency } from '../../context/CurrencyContext';
import './BookingSummary.css';

const BookingSummary = ({ booking }) => {
  const { formatFrom } = useCurrency();
  if (!booking) return null;

  const {
    hotelName,
    location,
    checkIn,
    checkOut,
    nights,
    roomType,
    guests,
    roomPrice,
    currency = 'GBP',
    taxes,
    serviceFee,
    total
  } = booking;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || nights || 1;
  };

  const nightsCount = calculateNights();
  const subtotal = (roomPrice || 0) * nightsCount;
  const taxesAmount = taxes || subtotal * 0.12; // 12% tax
  const serviceFeeAmount = serviceFee || subtotal * 0.05; // 5% service fee
  const finalTotal = subtotal + taxesAmount + serviceFeeAmount;

  return (
    <Card variant="elevated" className="booking-summary">
      <div className="booking-summary__header">
        <h2 className="booking-summary__title">Booking Summary</h2>
      </div>

      <div className="booking-summary__content">
        <div className="booking-summary__hotel">
          <h3 className="booking-summary__hotel-name">{hotelName || 'Grand Plaza Hotel'}</h3>
          <p className="booking-summary__hotel-location">{location || 'Paris, France'}</p>
        </div>

        <div className="booking-summary__dates">
          <div className="booking-summary__date-item">
            <span className="booking-summary__date-label">Check-in</span>
            <span className="booking-summary__date-value">{formatDate(checkIn)}</span>
          </div>
          <div className="booking-summary__date-item">
            <span className="booking-summary__date-label">Check-out</span>
            <span className="booking-summary__date-value">{formatDate(checkOut)}</span>
          </div>
          <div className="booking-summary__date-item">
            <span className="booking-summary__date-label">Nights</span>
            <span className="booking-summary__date-value">{nightsCount}</span>
          </div>
        </div>

        <div className="booking-summary__details">
          <div className="booking-summary__detail-item">
            <span className="booking-summary__detail-label">Room Type</span>
            <span className="booking-summary__detail-value">{roomType || 'Deluxe Room'}</span>
          </div>
          <div className="booking-summary__detail-item">
            <span className="booking-summary__detail-label">Guests</span>
            <span className="booking-summary__detail-value">{guests || 2} {guests === 1 ? 'Guest' : 'Guests'}</span>
          </div>
        </div>

        <div className="booking-summary__divider"></div>

        <div className="booking-summary__pricing">
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">Room ({nightsCount} {nightsCount === 1 ? 'night' : 'nights'})</span>
            <span className="booking-summary__price-value">{formatFrom(subtotal, currency)}</span>
          </div>
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">Taxes & Fees</span>
            <span className="booking-summary__price-value">{formatFrom(taxesAmount, currency)}</span>
          </div>
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">Service Fee</span>
            <span className="booking-summary__price-value">{formatFrom(serviceFeeAmount, currency)}</span>
          </div>
        </div>

        <div className="booking-summary__divider"></div>

        <div className="booking-summary__total">
          <span className="booking-summary__total-label">Total</span>
          <span className="booking-summary__total-value text-price-large">{formatFrom(finalTotal, currency)}</span>
        </div>
      </div>
    </Card>
  );
};

export default BookingSummary;
